import { bgColors, colors, theme } from '../styles/globalStyles';
import { styled } from '@mui/system';
import { Button, TextField, Typography } from '@mui/material';
import { ReactComponent as ArrowLeft } from '../images/left_arrow.svg';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import intg101Logo from '../images/intg101_logo_color.svg';
import { getUserData, registerUser } from '../api/auth';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { userAtom } from '../recoil/atoms/userAtom';
import { getWatchedStoriesData } from '../api/stories';
import StoryModal from '../components/modals/storyModal';
import stories from '../data/stories.json';
import { useKeyboardShift } from '../hooks/useKeyboardShift';
import { languagesAtom, selectedLanguageAtom } from '../recoil/atoms/languageAtom';
import Dropdown from '../components/dropdowns/dropdown';
import { ReactComponent as Globe } from '../images/globe.svg';
import data from '../data/data.json';

const Layout = styled('div')(({ background }) => ({
    padding: 17,
    minHeight: '100svh',

    display: 'flex',
    flexDirection: 'column',
    gap: '30px',

    backgroundSize: 'cover',
    backgroundBlendMode: 'multiply',
    borderRadius: 0,

    color: colors.white,

    [theme.breakpoints.up('xs')]: {
        maxWidth: 390,
        margin: '0 auto',
        borderRadius: 23,
    },
}));

const Header = styled('header')({
    paddingTop: 11,
    // paddingLeft: 11,

    display: 'flex',
    alignItems: 'center',
    gap: 11,
});

const RegistrationSection = styled('section')({
    display: 'flex',
    flexDirection: 'column',
    gap: '17px',
});

const Label = styled(Typography)({
    fontSize: 30,
    lineHeight: '120%',
});

const LabelCaption = styled(Typography)({
    fontSize: 12,
    lineHeight: 1.5,
    opacity: 0.7,
});

const Input = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: 10,
});

const ContinueButton = styled(Button)({
    width: '100%',
    height: 64,
    padding: '14px 17.5px',
    borderRadius: 23,
});

const RegistrationPage = () => {
    const [selectedLanguage, setSelectedLanguage] = useRecoilState(selectedLanguageAtom);
    const { registrationPage } = data[selectedLanguage];
    const translatedStories = stories[selectedLanguage];
    let navigate = useNavigate();
    const wrapperRef = useRef(null);

    const languages = useRecoilValue(languagesAtom);
    const selectedLanguageIndex = languages.findIndex(({ code }) => selectedLanguage === code);

    const bgColor = bgColors.purpleBlue;
    const color = colors.purple;

    const [companyName, setCompanyName] = useState('');
    const [name, setName] = useState(window.Telegram?.WebApp?.initDataUnsafe?.user?.first_name || '');
    const [role, setRole] = useState('');
    const [teamName, setTeamName] = useState('');
    const [participantsNumber, setParticipantsNumber] = useState('');

    const [hasWatchedStories, setHasWatchedStories] = useState(true);
    const storySetId = 3;

    const setUserInfo = useSetRecoilState(userAtom);
    const setLanguage = useSetRecoilState(selectedLanguageAtom);

    const [errorMessages, setErrorMessages] = useState({
        companyName: '',
        name: '',
        role: '',
        teamName: '',
        participantsNumber: '',
    });

    const [validating, setValidating] = useState(false);

    const [errors, setErrors] = useState({
        companyName: true,
        name: true,
        role: true,
        teamName: true,
        participantsNumber: true,
    });

    useKeyboardShift(wrapperRef, '17px');

    const resetErrorsMessages = () => {
        setErrorMessages({
            companyName: '',
            name: '',
            role: '',
            teamName: '',
            participantsNumber: '',
        });
    };

    const handleLanguageChange = ({ code }) => {
        setSelectedLanguage(code);
        localStorage.setItem('language', code);
    };

    const handleSubmit = () => {
        setValidating(true);

        const data = {
            telegram_id: window.Telegram?.WebApp?.initDataUnsafe?.user?.id,
            is_staff: true,
            companyName: companyName.trim().length !== 0 ? companyName : 'Invalid value (empty)',
            name: name.trim().length !== 0 ? name : 'Invalid value (empty)',
            role: role.trim().length !== 0 ? role : 'Invalid value (empty)',
            teamName: teamName.trim().length !== 0 ? teamName : 'Invalid value (empty)',
            participantsNumber: participantsNumber.trim().length !== 0 ? participantsNumber : 'Invalid value (empty)',
            teamLanguage: selectedLanguage,
        };

        const newErrors = { ...errors };
        const newErrorMessages = { ...errorMessages };

        for (const key in data) {
            newErrors[key] =
                data[key] === 'Invalid value (empty)' ||
                data[key] === 'Invalid email' ||
                data[key] === 'Invalid phone number';
            if (data[key] === 'Invalid value (empty)') {
                newErrorMessages[key] = registrationPage.inputs.errors.empty;
            } else if (data[key] === 'Invalid email') {
                newErrorMessages[key] = registrationPage.inputs.errors.email;
            } else if (data[key] === 'Invalid phone number') {
                newErrorMessages[key] = registrationPage.inputs.errors.phone;
            } else {
                newErrorMessages[key] = '';
            }
        }

        setErrorMessages(newErrorMessages);
        setErrors(newErrors);

        let errorExist = Object.values(newErrors).includes(true);
        if (errorExist || !data.telegram_id) {
            setErrors({ ...newErrors });
            return;
        }
        setValidating(false);
        resetErrorsMessages();

        registerUser(data)
            .then(({ data }) => {
                localStorage.setItem('auth-token', data.access);
                localStorage.setItem('refresh-token', data.refresh);
                getUserData()
                    .then((user) => {
                        setUserInfo(user);
                        setLanguage(user.language);

                        localStorage.setItem('user-id', user.userId);
                        localStorage.setItem('team-id', user.teamId);
                        localStorage.setItem('username', user.username);
                        localStorage.setItem('language', user.language);

                        if (user.isLeader) {
                            navigate(`/quiz?team=${user.team.sharing_url.slice(-6)}&isLeader=true`);
                            return;
                        }

                        navigate('/main');
                    })
                    .catch((error) => {
                        console.error('Ошибка получения пользователя', error);
                    });
            })
            .catch((err) => {
                console.error('Ошибка регистрации пользователя', err);
            });
    };

    useEffect(() => {
        getWatchedStoriesData(window.Telegram?.WebApp?.initDataUnsafe?.user?.id)
            .then((res) => {
                const watchedSets = res.data?.watched_story_sets;
                const isWatched = watchedSets.includes(storySetId);

                if (isWatched) {
                    return;
                }

                setHasWatchedStories(false);
            })
            .catch((err) => {
                console.error(err);
            });
    }, []);

    return (
        <Layout background={bgColor} ref={wrapperRef}>
            {!hasWatchedStories && (
                <StoryModal
                    storiesGroup={translatedStories.find((group) => group.id === storySetId)}
                    isAutoOpen={true}
                />
            )}
            <Header>
                <Button
                    startIcon={<ArrowLeft fill={color} />}
                    variant="coloredLight"
                    onClick={() => navigate('/auth')}
                    sx={{ color }}
                >
                    {registrationPage.backButton.text}
                </Button>
                <img src={intg101Logo} alt="intg101 logo" width={86} height={22} />
            </Header>
            <RegistrationSection>
                {languages && (
                    <Input>
                        <Label variant={'h1'}>{registrationPage.inputs.teamLanguage.label}</Label>
                        <Dropdown
                            startIcon={Globe}
                            variant="dropdownBlueMiddle"
                            options={languages.map(({ title, code }) => ({ code, name: title }))}
                            setSelectedOption={handleLanguageChange}
                            selected={selectedLanguageIndex}
                        />
                    </Input>
                )}
                <Input>
                    <Label variant={'h1'}>{registrationPage.inputs.companyName.label}</Label>
                    <TextField
                        value={companyName}
                        error={errors.companyName && validating}
                        onChange={(event) => setCompanyName(event.target.value)}
                        placeholder={registrationPage.inputs.companyName.placeholder}
                    />
                    {errorMessages.companyName && (
                        <Typography style={{ color: colors.red }}>{errorMessages.companyName}</Typography>
                    )}
                </Input>
                <Input>
                    <Label variant={'h1'}>{registrationPage.inputs.name.label}</Label>
                    <TextField
                        value={name}
                        error={errors.name && validating}
                        onChange={(event) => setName(event.target.value)}
                        placeholder={registrationPage.inputs.name.placeholder}
                    />
                    {errorMessages.name && <Typography style={{ color: colors.red }}>{errorMessages.name}</Typography>}
                </Input>
                <Input>
                    <Label variant={'h1'}>{registrationPage.inputs.role.label}</Label>
                    <TextField
                        value={role}
                        error={errors.role && validating}
                        onChange={(event) => setRole(event.target.value)}
                        placeholder={registrationPage.inputs.role.placeholder}
                    />
                    {errorMessages.role && <Typography style={{ color: colors.red }}>{errorMessages.role}</Typography>}
                </Input>
                <Input>
                    <Label variant={'h1'}>{registrationPage.inputs.teamName.label}</Label>
                    <TextField
                        value={teamName}
                        error={errors.teamName && validating}
                        onChange={(event) => setTeamName(event.target.value)}
                        placeholder={registrationPage.inputs.teamName.placeholder}
                    />
                    {errorMessages.teamName && (
                        <Typography style={{ color: colors.red }}>{errorMessages.teamName}</Typography>
                    )}
                </Input>
                <Input>
                    <Label variant={'h1'}>{registrationPage.inputs.participantsNumber.label}</Label>
                    <LabelCaption>{registrationPage.inputs.participantsNumber.caption}</LabelCaption>
                    <TextField
                        value={participantsNumber}
                        error={errors.participantsNumber && validating}
                        onChange={(event) => setParticipantsNumber(event.target.value)}
                        placeholder={registrationPage.inputs.participantsNumber.placeholder}
                    />
                    {errorMessages.participantsNumber && (
                        <Typography style={{ color: colors.red }}>{errorMessages.participantsNumber}</Typography>
                    )}
                </Input>
            </RegistrationSection>
            <ContinueButton variant="coloredLight" onClick={handleSubmit}>
                {registrationPage.continueButton.text}
            </ContinueButton>
        </Layout>
    );
};

export default RegistrationPage;
